// Aplicar Cédulas Directo — registrar cédulas ya aplicadas a mano (boleta física),
// SIN el flujo Bodega→Boomero ni notificaciones al ingeniero.
// Permite editar el detalle de productos fila por fila (eliminar, modificar cantidad,
// cambiar/agregar producto) y marcar la boleta como aplicada (descuenta inventario
// de la bodega Finca, sin validar saldos). NO toca las vistas/flujos actuales.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  ClipboardCheck, RefreshCw, Loader2, ChevronDown, ChevronRight, Trash2, Pencil,
  Plus, X, CheckCircle2, XCircle, Search,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { buildApiUrl } from '../../utils/apiUrl';
import { useEmpresaId } from '../../context/EmpresaContext';

interface BoletaProducto {
  id: number;
  articulo: string;
  nombre_producto: string;
  medida: string | null;
  dosis: number | null;
  cantidad_aplicar: number | null;
  inv_producto_id: number | null;
  es_agua: boolean;
  excluida: boolean;
  agregado_manualmente: boolean;
  orden: number;
}
interface Boleta {
  id: number;
  numero: string;
  semana: string;
  lote_num: string | null;
  finca_nombre: string | null;
  etapa_nombre: string | null;
  area_ha: number | null;
  plantas: number | null;
  estado: string;
  manual: boolean;
  motivo_cancelacion?: string | null;
  fecha_desde?: string | null;
  fecha_hasta?: string | null;
  fecha_aplicacion?: string | null;
  created_at?: string | null;
}
interface PlanProducto {
  id: number; articulo: string; nombre_producto: string;
  dosis: number | null; medida: string | null; departamento: string | null;
  // 'catalogo' = producto de inv_productos que NO está en el plan de manejo.
  // Llega con id negativo desde el backend y sin dosis planificada.
  origen?: 'plan' | 'catalogo';
}
// Datos que el boomero llena en campo (en este caso vinieron a mano en la fórmula impresa).
// Espeja las columnas de app_boleta_campo. Todo opcional.
interface CampoBoomero {
  caldo_sobrante: boolean; caldo_sobrante_litros: string;
  cultivo: boolean; semillero: boolean; area_barbecho: boolean;
  metodo_aspersion: boolean; metodo_inmersion: boolean; metodo_nebulizacion: boolean;
  boom_descripcion: string; rpm: string; presion_bar: string; marcha: string; operador: string;
  clima_nublado: boolean; clima_lluvioso: boolean; clima_soleado: boolean; clima_ventoso: boolean; clima_no_apto: boolean;
  observaciones: string;
}
const CAMPO_VACIO: CampoBoomero = {
  caldo_sobrante: false, caldo_sobrante_litros: '',
  cultivo: false, semillero: false, area_barbecho: false,
  metodo_aspersion: false, metodo_inmersion: false, metodo_nebulizacion: false,
  boom_descripcion: '', rpm: '', presion_bar: '', marcha: '', operador: '',
  clima_nublado: false, clima_lluvioso: false, clima_soleado: false, clima_ventoso: false, clima_no_apto: false,
  observaciones: '',
};

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmtN(v: number | null | undefined, dec = 2): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

// Estado actual de la cédula.
//  - 'pendiente'  : recién generada, sin tocar.
//  - 'en_proceso' : bodega/boomero ya avanzó el flujo normal.
//  - 'aplicada'   : ya se aplicó (a mano aquí o por el flujo). Solo lectura.
//  - 'cancelada'  : anulada. Solo lectura.
const ESTADO_BADGE: Record<string, { label: string; bg: string; text: string; border: string }> = {
  pendiente:  { label: 'Pendiente',  bg: 'var(--tone-warn-bg)',    text: 'var(--tone-warn-text)',    border: 'var(--tone-warn-border)' },
  en_proceso: { label: 'En proceso', bg: 'var(--tone-info-bg)',    text: 'var(--tone-info-text)',    border: 'var(--tone-info-border)' },
  aplicada:   { label: 'Aplicada',   bg: 'var(--tone-success-bg)', text: 'var(--tone-success-text)', border: 'var(--tone-success-border)' },
  cancelada:  { label: 'Cancelada',  bg: 'var(--surface-deep)',    text: 'var(--ink-faint)',         border: 'var(--line)' },
};

// Estados que puede editarse/aplicarse (escritura). El resto es solo lectura.
const ESTADOS_EDITABLES = new Set(['pendiente', 'en_proceso']);

type FiltroEst = 'todos' | 'pendiente' | 'en_proceso' | 'aplicada' | 'cancelada';
const FILTROS_EST: { key: FiltroEst; label: string }[] = [
  { key: 'todos',      label: 'Por aplicar' },
  { key: 'pendiente',  label: 'Pendientes' },
  { key: 'en_proceso', label: 'En proceso' },
  { key: 'aplicada',   label: 'Aplicadas' },
  { key: 'cancelada',  label: 'Canceladas' },
];

export function CedulasAplicarDirectoPage() {
  const empresaId = useEmpresaId();
  const [boletas, setBoletas] = useState<Boleta[]>([]);
  const [semana, setSemana] = useState<string>('');
  const [filtroEst, setFiltroEst] = useState<FiltroEst>('todos');
  const [abierta, setAbierta] = useState<number | null>(null);
  const [buscar, setBuscar] = useState('');
  const [productos, setProductos] = useState<Record<number, BoletaProducto[]>>({});
  const [planProds, setPlanProds] = useState<PlanProducto[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  // Modales
  const [editProd, setEditProd] = useState<{ boletaId: number; prod: BoletaProducto } | null>(null);
  const [addProd, setAddProd] = useState<{ boletaId: number } | null>(null);
  const [confirmAplicar, setConfirmAplicar] = useState<Boleta | null>(null);
  const [confirmCancelar, setConfirmCancelar] = useState<Boleta | null>(null);
  const [motivoCancelar, setMotivoCancelar] = useState('');

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  // Cargar boletas del flujo directo: por aplicar (pendiente/en_proceso) y ya
  // procesadas (aplicada/cancelada) para poder consultarlas/auditarlas.
  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('app_boletas')
        .select('id, numero, semana, lote_num, finca_nombre, etapa_nombre, area_ha, plantas, estado, manual, motivo_cancelacion, fecha_desde, fecha_hasta, fecha_aplicacion, created_at')
        .eq('empresa_id', empresaId)
        .in('estado', ['pendiente', 'en_proceso', 'aplicada', 'cancelada'])
        .order('semana', { ascending: false })
        .order('lote_num', { ascending: true });
      const list = (data || []) as Boleta[];
      setBoletas(list);
      // Semana por defecto: la más reciente disponible
      if (!semana && list.length) setSemana(list[0].semana);
    } finally {
      setLoading(false);
    }
  }, [empresaId, semana]);

  // Productos del plan (para cambiar/agregar) — vía backend (RLS bloquea anon)
  const cargarPlan = useCallback(async () => {
    try {
      const res = await fetch(buildApiUrl(`/aplicaciones/plan-productos?empresa_id=${empresaId}`));
      const j = await res.json();
      setPlanProds(j.ok ? (j.productos || []) : []);
    } catch { setPlanProds([]); }
  }, [empresaId]);

  useEffect(() => { cargar(); cargarPlan(); }, [cargar, cargarPlan]);

  const semanas = useMemo(
    () => [...new Set(boletas.map((b) => b.semana).filter(Boolean))].sort().reverse(),
    [boletas]
  );
  const boletasSemana = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    // Búsqueda por número: libre, ignora la semana (busca en todas las boletas).
    const base = q
      ? boletas.filter((b) => (b.numero || '').toLowerCase().includes(q))
      : boletas.filter((b) => !semana || b.semana === semana);
    // "Todas" = todas las pendientes de aplicar (pendiente/en_proceso). Las ya
    // aplicadas/canceladas solo salen si se filtran explícitamente (consulta/auditoría).
    if (filtroEst === 'todos') return base.filter((b) => ESTADOS_EDITABLES.has(b.estado));
    return base.filter((b) => b.estado === filtroEst);
  }, [boletas, semana, buscar, filtroEst]);

  // Conteo por estado sobre el conjunto visible ANTES de aplicar el filtro de estado
  // (respeta búsqueda por número y semana), para mostrar (n) en cada opción.
  const conteosEst = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    const base = q
      ? boletas.filter((b) => (b.numero || '').toLowerCase().includes(q))
      : boletas.filter((b) => !semana || b.semana === semana);
    const m: Record<string, number> = { todos: base.filter((b) => ESTADOS_EDITABLES.has(b.estado)).length };
    for (const b of base) m[b.estado] = (m[b.estado] ?? 0) + 1;
    return m;
  }, [boletas, semana, buscar]);

  const cargarProductos = useCallback(async (boletaId: number) => {
    const { data } = await supabase
      .from('app_boleta_productos')
      .select('id, articulo, nombre_producto, medida, dosis, cantidad_aplicar, inv_producto_id, es_agua, excluida, agregado_manualmente, orden')
      .eq('boleta_id', boletaId)
      .order('orden', { ascending: true });
    setProductos((prev) => ({ ...prev, [boletaId]: (data || []) as BoletaProducto[] }));
  }, []);

  const toggle = (b: Boleta) => {
    if (abierta === b.id) { setAbierta(null); return; }
    setAbierta(b.id);
    if (!productos[b.id]) cargarProductos(b.id);
  };

  // ── Acciones de producto ──────────────────────────────────────────────────
  // Timeout defensivo: si el servidor no responde (VPS saturado/lento), se aborta
  // el fetch y se lanza un error claro en vez de dejar el spinner girando eterno.
  const apiMut = async (url: string, method: string, body?: any) => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 20000); // 20 s
    try {
      const res = await fetch(buildApiUrl(url), {
        method,
        headers: { 'Content-Type': 'application/json', 'x-empresa-id': String(empresaId), ...(await authHeaders()) },
        body: body ? JSON.stringify({ empresa_id: empresaId, ...body }) : JSON.stringify({ empresa_id: empresaId }),
        signal: ctrl.signal,
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.error) throw new Error(j?.error || `Error HTTP ${res.status}`);
      return j;
    } catch (e: any) {
      if (e?.name === 'AbortError') throw new Error('El servidor no respondió (puede estar ocupado). Reintentá en unos segundos.');
      throw e;
    } finally {
      clearTimeout(timer);
    }
  };

  const eliminarFila = async (boletaId: number, prod: BoletaProducto) => {
    setBusy(true);
    try {
      await apiMut(`/aplicaciones/boletas/${boletaId}/producto/${prod.id}`, 'DELETE');
      await cargarProductos(boletaId);
      showToast('Producto eliminado.', true);
    } catch (e: any) { showToast(e.message, false); }
    finally { setBusy(false); }
  };

  const guardarEdit = async (boletaId: number, prodId: number, body: any) => {
    setBusy(true);
    try {
      await apiMut(`/aplicaciones/boletas/${boletaId}/producto/${prodId}`, 'PATCH', body);
      await cargarProductos(boletaId);
      setEditProd(null);
      showToast('Producto actualizado.', true);
    } catch (e: any) { showToast(e.message, false); }
    finally { setBusy(false); }
  };

  const agregar = async (boletaId: number, planProductoId: number, dosis?: number) => {
    setBusy(true);
    try {
      await apiMut(`/aplicaciones/boletas/${boletaId}/producto`, 'POST', { plan_producto_id: planProductoId, dosis });
      await cargarProductos(boletaId);
      setAddProd(null);
      showToast('Producto agregado.', true);
    } catch (e: any) { showToast(e.message, false); }
    finally { setBusy(false); }
  };

  const aplicar = async (b: Boleta, campo: CampoBoomero, fechaAplicacion: string) => {
    setBusy(true);
    try {
      const j = await apiMut(`/aplicaciones/boletas/${b.id}/aplicar-directo`, 'POST', { campo, fecha_aplicacion: fechaAplicacion });
      setConfirmAplicar(null);
      setAbierta(null);
      await cargar();
      const sinEnlace = (j.sin_enlace_inventario || []).length;
      showToast(
        `Cédula ${b.numero} aplicada el ${fmtFechaCr(j.fecha_aplicacion || fechaAplicacion)}. ${j.movimientos_creados} movimiento(s) de inventario` +
        (sinEnlace ? ` · ${sinEnlace} producto(s) sin enlace a inventario (no descontados).` : '.'),
        true
      );
    } catch (e: any) { showToast(e.message, false); }
    finally { setBusy(false); }
  };

  const cancelar = async (b: Boleta) => {
    setBusy(true);
    try {
      await apiMut(`/aplicaciones/boletas/${b.id}/cancelar-directo`, 'POST', { motivo: motivoCancelar.trim() });
      setConfirmCancelar(null);
      setMotivoCancelar('');
      setAbierta(null);
      await cargar();
      showToast(`Cédula ${b.numero} cancelada.`, true);
    } catch (e: any) { showToast(e.message, false); }
    finally { setBusy(false); }
  };

  // ── Estilos ─────────────────────────────────────────────────────────────
  const inputCls = 'bg-transparent outline-none text-sm';
  const cellInput: React.CSSProperties = { color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 6, padding: '6px 8px', background: 'var(--surface-raised)' };
  const optStyle: React.CSSProperties = { backgroundColor: 'var(--surface-raised)', color: 'var(--ink)' };

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: 'var(--surface)' }}>
      {/* Encabezado */}
      <div className="px-5 py-3 shrink-0 flex items-center gap-4 flex-wrap" style={{ borderBottom: '1px solid var(--line)' }}>
        <div className="flex items-center gap-2">
          <ClipboardCheck size={20} style={{ color: 'var(--emp-accent-txt)' }} />
          <div>
            <h1 className="text-base font-bold" style={{ color: 'var(--ink)' }}>Aplicar Cédulas Directo</h1>
            <p className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>Registrar cédulas aplicadas a mano (boleta física) — sin Bodega/Boomero</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          {/* Buscar por número de cédula (libre: ignora la semana) */}
          <div className="relative">
            <Search size={13} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-faint)' }} />
            <input className={inputCls} style={{ ...cellInput, padding: '5px 8px 5px 26px', width: '11rem' }}
              placeholder="Buscar cédula (N°)…" value={buscar}
              onChange={(e) => { setBuscar(e.target.value); setAbierta(null); }} />
            {buscar && (
              <button onClick={() => setBuscar('')} title="Limpiar"
                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-faint)' }}>
                <X size={13} />
              </button>
            )}
          </div>
          <label className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--ink-muted)' }}>
            Estado
            <select className={inputCls} style={{ ...cellInput, padding: '4px 8px', colorScheme: 'light dark' }}
              value={filtroEst} onChange={(e) => { setFiltroEst(e.target.value as FiltroEst); setAbierta(null); }}>
              {FILTROS_EST.map((f) => (
                <option key={f.key} value={f.key} style={optStyle}>
                  {f.label} ({conteosEst[f.key] ?? 0})
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--ink-muted)', opacity: buscar.trim() ? 0.5 : 1 }}>
            Semana
            <select className={inputCls} style={{ ...cellInput, padding: '4px 8px', colorScheme: 'light dark' }}
              value={semana} disabled={!!buscar.trim()} onChange={(e) => { setSemana(e.target.value); setAbierta(null); }}>
              <option value="" style={optStyle}>Todas</option>
              {semanas.map((s) => <option key={s} value={s} style={optStyle}>{s}</option>)}
            </select>
          </label>
          <button onClick={() => { cargar(); cargarPlan(); }} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Recargar
          </button>
        </div>
      </div>

      {toast && (
        <div className="px-5 py-2 shrink-0 text-xs font-medium"
          style={{ background: toast.ok ? 'var(--tone-success-bg)' : '#fff4e5', color: toast.ok ? 'var(--tone-success-text)' : '#a15c00', borderBottom: `1px solid ${toast.ok ? 'var(--tone-success-border)' : '#ffb74d'}` }}>
          {toast.msg}
        </div>
      )}

      <div className="flex-1 overflow-auto p-5 space-y-2">
        <p className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>
          Estas cédulas se aplicaron a mano. Ajustá el detalle según la boleta física y marcá <b>Aplicar</b> — descuenta inventario de bodega <b>Finca</b>.
        </p>

        {boletasSemana.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <ClipboardCheck size={34} style={{ color: 'var(--ink-faint)' }} />
            <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
              {buscar.trim()
                ? `No se encontró ninguna cédula con "${buscar.trim()}".`
                : `No hay cédulas ${filtroEst !== 'todos' ? `en estado "${FILTROS_EST.find((f) => f.key === filtroEst)?.label.toLowerCase()}"` : ''} ${semana ? `en la semana ${semana}` : ''}.`.replace(/\s+/g, ' ').trim()}
            </p>
          </div>
        )}

        {boletasSemana.map((b) => {
          const abiertoEsta = abierta === b.id;
          const prods = (productos[b.id] || []).filter((p) => !p.es_agua);
          // Cédulas aplicadas/canceladas son SOLO LECTURA: no se editan ni re-aplican.
          const editable = ESTADOS_EDITABLES.has(b.estado);
          return (
            <div key={b.id} className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--line-dim)' }}>
              {/* Cabecera persiana */}
              <button onClick={() => toggle(b)} className="w-full flex items-center gap-3 px-3 py-2.5 text-left" style={{ background: 'var(--surface-deep)' }}>
                {abiertoEsta ? <ChevronDown size={16} style={{ color: 'var(--emp-accent-txt)' }} /> : <ChevronRight size={16} style={{ color: 'var(--ink-faint)' }} />}
                <span className="text-sm font-bold font-mono" style={{ color: 'var(--ink)' }}>#{b.numero}</span>
                <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Lote {b.lote_num} · {b.etapa_nombre}</span>
                {(() => {
                  const est = ESTADO_BADGE[b.estado] ?? { label: b.estado, bg: 'var(--surface-raised)', text: 'var(--ink-muted)', border: 'var(--line-dim)' };
                  return (
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ background: est.bg, color: est.text, border: `1px solid ${est.border}` }}>
                      {est.label}
                    </span>
                  );
                })()}
                {b.manual && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)' }}>Manual</span>}
                <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{fmtN(b.area_ha)} ha</span>
                <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full" style={{ background: 'var(--surface-raised)', color: 'var(--ink-muted)', border: '1px solid var(--line-dim)' }}>
                  Sem. {b.semana}
                </span>
              </button>

              {abiertoEsta && (
                <div className="p-3" style={{ background: 'var(--surface-raised)' }}>
                  {!productos[b.id] ? (
                    <div className="flex items-center gap-2 py-4 text-xs" style={{ color: 'var(--ink-faint)' }}>
                      <Loader2 size={14} className="animate-spin" /> Cargando productos…
                    </div>
                  ) : (
                    <>
                      <table className="w-full text-sm">
                        <thead>
                          <tr style={{ color: 'var(--ink-faint)' }}>
                            <th className="text-left font-semibold text-[10px] uppercase tracking-wider py-1 px-2">Producto</th>
                            <th className="text-right font-semibold text-[10px] uppercase tracking-wider py-1 px-2">Dosis</th>
                            <th className="text-right font-semibold text-[10px] uppercase tracking-wider py-1 px-2">Cant. aplicar</th>
                            <th className="text-center font-semibold text-[10px] uppercase tracking-wider py-1 px-2">Inv.</th>
                            <th className="py-1 px-2" style={{ width: '5.5rem' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {prods.map((p) => (
                            <tr key={p.id} style={{ borderTop: '1px solid var(--line-dim)', opacity: p.excluida ? 0.45 : 1 }}>
                              <td className="py-1.5 px-2" style={{ color: 'var(--ink)' }}>
                                <span style={{ textDecoration: p.excluida ? 'line-through' : 'none' }}>{p.nombre_producto}</span>
                                {p.agregado_manualmente && <span className="ml-1.5 text-[9px] px-1 py-px rounded" style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)' }}>+manual</span>}
                              </td>
                              <td className="py-1.5 px-2 text-right font-mono text-xs" style={{ color: 'var(--ink-muted)' }}>{fmtN(p.dosis, 3)} {p.medida || ''}</td>
                              <td className="py-1.5 px-2 text-right font-mono text-xs" style={{ color: 'var(--ink)' }}>{fmtN(p.cantidad_aplicar, 2)} {p.medida || ''}</td>
                              <td className="py-1.5 px-2 text-center">
                                {p.inv_producto_id
                                  ? <span className="text-[9px] px-1.5 py-px rounded" style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)' }}>✓</span>
                                  : <span className="text-[9px] px-1.5 py-px rounded" style={{ background: '#fff4e5', color: '#a15c00' }}>sin inv.</span>}
                              </td>
                              <td className="py-1.5 px-2">
                                {editable && !p.excluida && (
                                  <div className="flex items-center justify-end gap-1">
                                    <button onClick={() => setEditProd({ boletaId: b.id, prod: p })} title="Modificar"
                                      className="p-1 rounded" style={{ border: '1px solid var(--line-dim)', color: 'var(--emp-accent-txt)' }}><Pencil size={12} /></button>
                                    <button onClick={() => eliminarFila(b.id, p)} title="Eliminar fila" disabled={busy}
                                      className="p-1 rounded" style={{ border: '1px solid var(--line-dim)', color: 'var(--tone-error-text)' }}><Trash2 size={12} /></button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {editable ? (
                        <div className="flex items-center gap-2 mt-3">
                          <button onClick={() => setAddProd({ boletaId: b.id })}
                            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
                            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
                            <Plus size={13} /> Agregar producto
                          </button>
                          <button onClick={() => { setMotivoCancelar(''); setConfirmCancelar(b); }} disabled={busy}
                            className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
                            style={{ background: 'var(--surface-deep)', border: '1px solid var(--tone-error-border)', color: 'var(--tone-error-text)' }}>
                            <XCircle size={13} /> Cancelar cédula
                          </button>
                          <button onClick={() => setConfirmAplicar(b)} disabled={busy}
                            className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-semibold"
                            style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-on)' }}>
                            <CheckCircle2 size={14} /> Aplicar cédula
                          </button>
                        </div>
                      ) : (
                        <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded text-[11px]"
                          style={{ background: 'var(--surface-deep)', border: '1px solid var(--line-dim)', color: 'var(--ink-muted)' }}>
                          {b.estado === 'aplicada'
                            ? <><CheckCircle2 size={13} style={{ color: 'var(--tone-success-text)' }} /> Cédula ya aplicada{b.fecha_aplicacion ? ` en campo el ${fmtFechaCr(b.fecha_aplicacion)}` : ''} — solo lectura.</>
                            : <><XCircle size={13} style={{ color: 'var(--ink-faint)' }} /> Cédula cancelada — solo lectura.{b.motivo_cancelacion ? <> Motivo: <b>{b.motivo_cancelacion}</b></> : ''}</>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal modificar producto */}
      {editProd && ReactDOM.createPortal(
        <EditProductoModal
          prod={editProd.prod} planProds={planProds} busy={busy} optStyle={optStyle} cellInput={cellInput} inputCls={inputCls}
          onClose={() => setEditProd(null)}
          onSave={(body) => guardarEdit(editProd.boletaId, editProd.prod.id, body)}
        />, document.body)}

      {/* Modal agregar producto */}
      {addProd && ReactDOM.createPortal(
        <AddProductoModal
          planProds={planProds} busy={busy} optStyle={optStyle} cellInput={cellInput} inputCls={inputCls}
          onClose={() => setAddProd(null)}
          onAdd={(pid, dosis) => agregar(addProd.boletaId, pid, dosis)}
        />, document.body)}

      {/* Modal aplicar + datos de campo del boomero (fórmula impresa) */}
      {confirmAplicar && ReactDOM.createPortal(
        <AplicarCampoModal
          boleta={confirmAplicar} busy={busy} cellInput={cellInput} inputCls={inputCls}
          onClose={() => setConfirmAplicar(null)}
          onAplicar={(campo, fecha) => aplicar(confirmAplicar, campo, fecha)}
        />, document.body)}

      {/* Modal confirmar cancelar */}
      {confirmCancelar && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" style={{ background: 'rgba(8,12,22,0.72)' }} onClick={() => !busy && setConfirmCancelar(null)}>
          <div className="rounded-xl w-full max-w-sm overflow-hidden" style={{ background: 'var(--surface-base)', border: '1px solid var(--tone-error-border)', boxShadow: '0 24px 60px rgba(0,0,0,0.55)' }} onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--line)' }}>
              <XCircle size={18} style={{ color: 'var(--tone-error-text)' }} />
              <h2 className="text-sm font-bold flex-1" style={{ color: 'var(--ink)' }}>Cancelar cédula</h2>
              <button onClick={() => !busy && setConfirmCancelar(null)} className="p-1 rounded" style={{ color: 'var(--ink-faint)' }}><X size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-2">
              <p className="text-sm" style={{ color: 'var(--ink)' }}>
                ¿Cancelar la cédula <b>#{confirmCancelar.numero}</b> (Lote {confirmCancelar.lote_num})?
              </p>
              <p className="text-[12px]" style={{ color: 'var(--ink-muted)' }}>
                Se marca como <b>cancelada</b> (no se aplicó). No descuenta ni toca inventario. Saldrá de la lista de pendientes.
              </p>
              <label className="block text-[11px] font-semibold pt-1" style={{ color: 'var(--ink-muted)' }}>Motivo de la cancelación *</label>
              <textarea className={inputCls} rows={3} autoFocus maxLength={500} value={motivoCancelar}
                onChange={(e) => setMotivoCancelar(e.target.value)}
                placeholder="Ej.: se duplicó la cédula, no se aplicó por lluvia…"
                style={{ ...cellInput, width: '100%', resize: 'none' }} />
            </div>
            <div className="px-5 py-3 flex items-center justify-end gap-2" style={{ borderTop: '1px solid var(--line)' }}>
              <button onClick={() => setConfirmCancelar(null)} disabled={busy} className="px-3 py-2 rounded text-xs font-medium" style={{ border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>Volver</button>
              <button onClick={() => cancelar(confirmCancelar)} disabled={busy || !motivoCancelar.trim()} className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-semibold" style={{ background: 'var(--tone-error-text)', color: '#fff', opacity: busy || !motivoCancelar.trim() ? 0.5 : 1 }}>
                {busy ? <Loader2 size={13} className="animate-spin" /> : <XCircle size={13} />} Cancelar cédula
              </button>
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}

// ── Modal: modificar producto (dosis/cantidad o cambiar producto) ────────────
function EditProductoModal({ prod, planProds, busy, optStyle, cellInput, inputCls, onClose, onSave }: {
  prod: BoletaProducto; planProds: PlanProducto[]; busy: boolean;
  optStyle: React.CSSProperties; cellInput: React.CSSProperties; inputCls: string;
  onClose: () => void; onSave: (body: any) => void;
}) {
  const [modo, setModo] = useState<'cantidad' | 'cambiar'>('cantidad');
  const [dosis, setDosis] = useState<string>(prod.dosis != null ? String(prod.dosis) : '');
  const [cantidad, setCantidad] = useState<string>(prod.cantidad_aplicar != null ? String(prod.cantidad_aplicar) : '');
  const [nuevoProd, setNuevoProd] = useState<string>('');

  const submit = () => {
    if (modo === 'cambiar') {
      if (!nuevoProd) return;
      onSave({ plan_producto_id: Number(nuevoProd), ...(dosis !== '' ? { dosis: Number(dosis) } : {}) });
    } else {
      // Cantidad: si tocó dosis manda dosis (recalcula); si tocó cantidad manda override
      const body: any = {};
      if (cantidad !== '' && Number(cantidad) !== Number(prod.cantidad_aplicar)) body.cantidad_aplicar = Number(cantidad);
      if (dosis !== '' && Number(dosis) !== Number(prod.dosis)) body.dosis = Number(dosis);
      if (Object.keys(body).length === 0) { onClose(); return; }
      onSave(body);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" style={{ background: 'rgba(8,12,22,0.72)' }} onClick={() => !busy && onClose()}>
      <div className="rounded-xl w-full max-w-md overflow-hidden" style={{ background: 'var(--surface-base)', border: '1px solid var(--line)', boxShadow: '0 24px 60px rgba(0,0,0,0.55)' }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--line)' }}>
          <Pencil size={18} style={{ color: 'var(--emp-accent-txt)' }} />
          <h2 className="text-sm font-bold flex-1" style={{ color: 'var(--ink)' }}>Modificar producto</h2>
          <button onClick={() => !busy && onClose()} className="p-1 rounded" style={{ color: 'var(--ink-faint)' }}><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Actual: <b style={{ color: 'var(--ink)' }}>{prod.nombre_producto}</b></p>
          <div className="flex gap-2">
            <button onClick={() => setModo('cantidad')} className="flex-1 px-3 py-1.5 rounded text-xs font-medium"
              style={{ background: modo === 'cantidad' ? 'var(--emp-accent)' : 'var(--surface-deep)', color: modo === 'cantidad' ? 'var(--emp-accent-on)' : 'var(--ink-muted)', border: '1px solid var(--line)' }}>
              Dosis / cantidad
            </button>
            <button onClick={() => setModo('cambiar')} className="flex-1 px-3 py-1.5 rounded text-xs font-medium"
              style={{ background: modo === 'cambiar' ? 'var(--emp-accent)' : 'var(--surface-deep)', color: modo === 'cambiar' ? 'var(--emp-accent-on)' : 'var(--ink-muted)', border: '1px solid var(--line)' }}>
              Cambiar producto
            </button>
          </div>

          {modo === 'cambiar' && (
            <div>
              <p className="text-[9px] uppercase tracking-widest mb-1" style={{ color: 'var(--ink-faint)' }}>Producto sustituto</p>
              <select className={inputCls} style={{ ...cellInput, width: '100%', colorScheme: 'light dark' }} value={nuevoProd} onChange={(e) => setNuevoProd(e.target.value)}>
                <option value="" style={optStyle}>— Seleccionar —</option>
                {planProds.map((p) => <option key={p.id} value={String(p.id)} style={optStyle}>{p.nombre_producto}</option>)}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[9px] uppercase tracking-widest mb-1" style={{ color: 'var(--ink-faint)' }}>Dosis {modo === 'cantidad' ? '(recalcula)' : '(opcional)'}</p>
              <input type="number" step="0.0001" className={inputCls} style={{ ...cellInput, width: '100%' }} value={dosis} onChange={(e) => setDosis(e.target.value)} />
            </div>
            {modo === 'cantidad' && (
              <div>
                <p className="text-[9px] uppercase tracking-widest mb-1" style={{ color: 'var(--ink-faint)' }}>Cant. aplicar (directo)</p>
                <input type="number" step="0.0001" className={inputCls} style={{ ...cellInput, width: '100%' }} value={cantidad} onChange={(e) => setCantidad(e.target.value)} />
              </div>
            )}
          </div>
        </div>
        <div className="px-5 py-3 flex items-center justify-end gap-2" style={{ borderTop: '1px solid var(--line)' }}>
          <button onClick={onClose} disabled={busy} className="px-3 py-2 rounded text-xs font-medium" style={{ border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>Cancelar</button>
          <button onClick={submit} disabled={busy} className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-semibold" style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-on)', opacity: busy ? 0.6 : 1 }}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Pencil size={13} />} Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal: agregar producto ──────────────────────────────────────────────────
function AddProductoModal({ planProds, busy, optStyle, cellInput, inputCls, onClose, onAdd }: {
  planProds: PlanProducto[]; busy: boolean;
  optStyle: React.CSSProperties; cellInput: React.CSSProperties; inputCls: string;
  onClose: () => void; onAdd: (planProductoId: number, dosis?: number) => void;
}) {
  const [prodId, setProdId] = useState<string>('');
  const [dosis, setDosis] = useState<string>('');
  const [busqueda, setBusqueda] = useState('');
  const sel = planProds.find((p) => String(p.id) === prodId);

  // Filtro por código o nombre, sin acentos (el catálogo mezcla "ACIDO"/"ÁCIDO").
  const norm = (s: unknown) =>
    String(s ?? '').toLowerCase().normalize('NFD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '');
  const q = norm(busqueda.trim());
  const filtrados = q
    ? planProds.filter((p) => norm(p.nombre_producto).includes(q) || norm(p.articulo).includes(q))
    : planProds;

  // Un producto del catálogo no trae dosis del plan: sin dosis no se puede calcular.
  const faltaDosis = sel?.origen === 'catalogo' && !(Number(dosis) > 0);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" style={{ background: 'rgba(8,12,22,0.72)' }} onClick={() => !busy && onClose()}>
      <div className="rounded-xl w-full max-w-md overflow-hidden" style={{ background: 'var(--surface-base)', border: '1px solid var(--line)', boxShadow: '0 24px 60px rgba(0,0,0,0.55)' }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--line)' }}>
          <Plus size={18} style={{ color: 'var(--emp-accent-txt)' }} />
          <h2 className="text-sm font-bold flex-1" style={{ color: 'var(--ink)' }}>Agregar producto</h2>
          <button onClick={() => !busy && onClose()} className="p-1 rounded" style={{ color: 'var(--ink-faint)' }}><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <p className="text-[9px] uppercase tracking-widest mb-1" style={{ color: 'var(--ink-faint)' }}>Producto</p>
            <div className="relative mb-2">
              <Search size={13} style={{ color: 'var(--ink-faint)', position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input
                type="text" autoFocus value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por código o nombre…"
                className={inputCls}
                style={{ ...cellInput, width: '100%', paddingLeft: 28, paddingRight: 26 }}
              />
              {busqueda && (
                <button onClick={() => setBusqueda('')} aria-label="Limpiar"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs" style={{ color: 'var(--ink-muted)' }}>✕</button>
              )}
            </div>
            {/* Lista filtrada en vez de <select> nativo: el catálogo pasa de 100 items. */}
            <div className="rounded overflow-y-auto" style={{ border: '1px solid var(--line)', maxHeight: 220 }}>
              {filtrados.length === 0 ? (
                <p className="text-[11px] text-center py-6" style={{ color: 'var(--ink-faint)' }}>
                  Sin coincidencias para “{busqueda}”.
                </p>
              ) : filtrados.map((p) => {
                const activo = String(p.id) === prodId;
                return (
                  <button
                    key={p.id}
                    onClick={() => { setProdId(String(p.id)); setDosis(p.dosis != null ? String(p.dosis) : ''); }}
                    className="w-full text-left px-2.5 py-1.5 flex items-center gap-2 transition-colors"
                    style={{
                      background: activo ? 'var(--emp-accent-bg)' : 'transparent',
                      borderLeft: activo ? '2px solid var(--emp-accent)' : '2px solid transparent',
                    }}>
                    <span className="font-mono text-[10px] shrink-0" style={{ color: 'var(--ink-faint)', minWidth: 34 }}>
                      {p.articulo || '—'}
                    </span>
                    <span className="text-[11px] flex-1 truncate" style={{ color: activo ? 'var(--emp-accent-txt)' : 'var(--ink)' }}>
                      {p.nombre_producto}
                    </span>
                    {p.origen === 'catalogo' && (
                      <span className="text-[9px] px-1 py-0.5 rounded shrink-0"
                        title="Del catálogo de inventario — no está en el plan de manejo"
                        style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-faint)' }}>
                        fuera de plan
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-widest mb-1" style={{ color: 'var(--ink-faint)' }}>
              Dosis {sel?.medida ? `(${sel.medida})` : ''}
              {sel?.origen === 'catalogo' && <span style={{ color: 'var(--tone-warn-text)' }}> · requerida</span>}
            </p>
            <input type="number" step="0.0001" className={inputCls} style={{ ...cellInput, width: '100%' }} value={dosis} onChange={(e) => setDosis(e.target.value)}
              placeholder={sel?.origen === 'catalogo' ? 'Sin dosis en el plan: escribila' : 'Dosis del plan por defecto'} />
            {sel?.origen === 'catalogo' && (
              <p className="text-[10px] mt-1" style={{ color: 'var(--ink-faint)' }}>
                Producto fuera del plan de manejo: los datos agronómicos se toman del catálogo.
              </p>
            )}
          </div>
        </div>
        <div className="px-5 py-3 flex items-center justify-end gap-2" style={{ borderTop: '1px solid var(--line)' }}>
          <button onClick={onClose} disabled={busy} className="px-3 py-2 rounded text-xs font-medium" style={{ border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>Cancelar</button>
          <button onClick={() => prodId && onAdd(Number(prodId), dosis !== '' ? Number(dosis) : undefined)} disabled={busy || !prodId || faltaDosis}
            title={faltaDosis ? 'Los productos fuera del plan requieren una dosis' : undefined}
            className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-semibold" style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-on)', opacity: (busy || !prodId || faltaDosis) ? 0.6 : 1 }}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Agregar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal: aplicar cédula + datos de campo del boomero (fórmula impresa) ──────
function AplicarCampoModal({ boleta, busy, cellInput, inputCls, onClose, onAplicar }: {
  boleta: Boleta; busy: boolean; cellInput: React.CSSProperties; inputCls: string;
  onClose: () => void; onAplicar: (campo: CampoBoomero, fechaAplicacion: string) => void;
}) {
  const [f, setF] = useState<CampoBoomero>(CAMPO_VACIO);
  // Día en que se aplicó EN CAMPO (la boleta llega a la oficina con atraso).
  const hoy = hoyCr();
  const minFecha = boleta.created_at ? fechaCr(boleta.created_at) : '';
  const [fecha, setFecha] = useState(hoy);
  const fechaInvalida = !fecha || fecha > hoy || (!!minFecha && fecha < minFecha);
  const fueraDeSemana = !!fecha && !!boleta.fecha_desde && !!boleta.fecha_hasta
    && (fecha < boleta.fecha_desde || fecha > boleta.fecha_hasta);
  const set = <K extends keyof CampoBoomero>(k: K, v: CampoBoomero[K]) => setF((p) => ({ ...p, [k]: v }));

  const Check = ({ k, label }: { k: keyof CampoBoomero; label: string }) => (
    <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs" style={{ color: 'var(--ink)' }}>
      <input type="checkbox" checked={!!f[k]} onChange={(e) => set(k, e.target.checked as any)} />
      {label}
    </label>
  );
  const Lbl = ({ children }: { children: React.ReactNode }) => (
    <p className="text-[9px] uppercase tracking-widest mb-1" style={{ color: 'var(--ink-faint)' }}>{children}</p>
  );
  const Grupo = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="rounded-lg p-3" style={{ border: '1px solid var(--line-dim)', background: 'var(--surface-deep)' }}>
      <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--ink-muted)' }}>{title}</p>
      {children}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" style={{ background: 'rgba(8,12,22,0.72)' }} onClick={() => !busy && onClose()}>
      <div className="rounded-xl w-full max-w-2xl overflow-hidden flex flex-col" style={{ background: 'var(--surface-base)', border: '1px solid var(--line)', boxShadow: '0 24px 60px rgba(0,0,0,0.55)', maxHeight: '92vh' }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 flex items-center gap-2 shrink-0" style={{ borderBottom: '1px solid var(--line)' }}>
          <CheckCircle2 size={18} style={{ color: 'var(--emp-accent-txt)' }} />
          <h2 className="text-sm font-bold flex-1" style={{ color: 'var(--ink)' }}>Aplicar cédula #{boleta.numero} · Lote {boleta.lote_num}</h2>
          <button onClick={() => !busy && onClose()} className="p-1 rounded" style={{ color: 'var(--ink-faint)' }}><X size={18} /></button>
        </div>

        <div className="px-5 py-4 space-y-3 overflow-auto">
          <p className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>
            Datos de campo (de la fórmula impresa). Todos opcionales salvo la fecha. Al aplicar descuenta inventario de bodega <b>Finca</b> con la fecha de aplicación.
          </p>

          <div className="rounded-lg p-3 flex flex-wrap items-end gap-3" style={{ border: '1px solid var(--line-dim)', background: 'var(--surface-deep)' }}>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: 'var(--ink-muted)' }}>Fecha de aplicación en campo *</p>
              <input type="date" className={inputCls} style={{ ...cellInput, width: 170 }} value={fecha} max={hoy} min={minFecha || undefined}
                onChange={(e) => setFecha(e.target.value)} />
            </div>
            <div className="text-[11px] flex-1 min-w-[200px]" style={{ color: 'var(--ink-muted)' }}>
              Semana {boleta.semana}{boleta.fecha_desde && boleta.fecha_hasta ? ` (${fmtFechaCr(boleta.fecha_desde)} al ${fmtFechaCr(boleta.fecha_hasta)})` : ''}.
              {fecha !== hoy && !fechaInvalida && <span> Se registra con <b>{Math.round((Date.parse(hoy) - Date.parse(fecha)) / 86400000)} día(s) de atraso</b>.</span>}
              {fechaInvalida && <span style={{ color: 'var(--danger, #f87171)' }}> La fecha no puede ser futura{minFecha ? ` ni anterior al ${fmtFechaCr(minFecha)} (creación de la boleta)` : ''}.</span>}
              {fueraDeSemana && !fechaInvalida && <span style={{ color: 'var(--warn, #fbbf24)' }}> Ojo: la fecha está fuera de la semana de la boleta.</span>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Grupo title="Caldo sobrante">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 cursor-pointer text-xs" style={{ color: 'var(--ink)' }}>
                  <input type="radio" name="caldo" checked={f.caldo_sobrante} onChange={() => set('caldo_sobrante', true)} /> Sí
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs" style={{ color: 'var(--ink)' }}>
                  <input type="radio" name="caldo" checked={!f.caldo_sobrante} onChange={() => set('caldo_sobrante', false)} /> No
                </label>
              </div>
              {f.caldo_sobrante && (
                <input type="number" step="0.01" className={inputCls} style={{ ...cellInput, width: '100%', marginTop: 8 }} placeholder="Litros" value={f.caldo_sobrante_litros} onChange={(e) => set('caldo_sobrante_litros', e.target.value)} />
              )}
            </Grupo>
            <Grupo title="Aplicado en">
              <div className="flex flex-col gap-1.5">
                <Check k="cultivo" label="Cultivo" />
                <Check k="semillero" label="Semillero" />
                <Check k="area_barbecho" label="Área de barbecho" />
              </div>
            </Grupo>
            <Grupo title="Método de aplicación">
              <div className="flex flex-col gap-1.5">
                <Check k="metodo_aspersion" label="Aspersión" />
                <Check k="metodo_inmersion" label="Inmersión" />
                <Check k="metodo_nebulizacion" label="Nebulización" />
              </div>
            </Grupo>
          </div>

          <Grupo title="Información de calibración">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div><Lbl>Equipo</Lbl><input className={inputCls} style={{ ...cellInput, width: '100%' }} value={f.boom_descripcion} onChange={(e) => set('boom_descripcion', e.target.value)} /></div>
              <div><Lbl>RPM</Lbl><input type="number" className={inputCls} style={{ ...cellInput, width: '100%' }} value={f.rpm} onChange={(e) => set('rpm', e.target.value)} /></div>
              <div><Lbl>Presión</Lbl><input type="number" step="0.1" className={inputCls} style={{ ...cellInput, width: '100%' }} value={f.presion_bar} onChange={(e) => set('presion_bar', e.target.value)} /></div>
              <div><Lbl>Marcha</Lbl><input className={inputCls} style={{ ...cellInput, width: '100%' }} value={f.marcha} onChange={(e) => set('marcha', e.target.value)} /></div>
              <div><Lbl>Operador</Lbl><input className={inputCls} style={{ ...cellInput, width: '100%' }} value={f.operador} onChange={(e) => set('operador', e.target.value)} /></div>
            </div>
          </Grupo>

          <Grupo title="Condiciones climáticas">
            <div className="flex items-center flex-wrap gap-4">
              <Check k="clima_nublado" label="Nublado" />
              <Check k="clima_lluvioso" label="Lluvioso" />
              <Check k="clima_soleado" label="Soleado" />
              <Check k="clima_ventoso" label="Ventoso" />
              <Check k="clima_no_apto" label="No Apto" />
            </div>
          </Grupo>

          <div>
            <Lbl>Observaciones</Lbl>
            <textarea className={inputCls} style={{ ...cellInput, width: '100%', minHeight: 56, resize: 'vertical' }} value={f.observaciones} onChange={(e) => set('observaciones', e.target.value)} />
          </div>
        </div>

        <div className="px-5 py-3 flex items-center justify-end gap-2 shrink-0" style={{ borderTop: '1px solid var(--line)' }}>
          <button onClick={onClose} disabled={busy} className="px-3 py-2 rounded text-xs font-medium" style={{ border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>Cancelar</button>
          <button onClick={() => onAplicar(f, fecha)} disabled={busy || fechaInvalida} className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-semibold" style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-on)', opacity: (busy || fechaInvalida) ? 0.6 : 1 }}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Aplicar cédula
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Fechas en hora de Costa Rica ─────────────────────────────────────────────
function hoyCr(): string { return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Costa_Rica' }); }
function fechaCr(iso: string): string { return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Costa_Rica' }); }
function fmtFechaCr(ymd?: string | null): string {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : String(ymd);
}
